import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../services/notification/toast.service';
import { InvoiceRefundView } from '../invoice-refund-view.model';

export interface RefundReceiptDialogData {
  invoice: any;
  payment: any | null;
  refund?: InvoiceRefundView;
  refundTypeLabel?: string | null;
  refundStatusLabel?: string | null;
  refundAuthorizationUnavailableLabel?: string;
  currency: string | null;
}

@Component({
  selector: 'app-refund-receipt-dialog',
  templateUrl: './refund-receipt-dialog.component.html',
  styleUrls: ['./refund-receipt-dialog.component.scss'],
  standalone: false,
})
export class RefundReceiptDialogComponent {
  private static readonly DEFAULT_UNAVAILABLE_LABEL = 'No disponible';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RefundReceiptDialogData,
    private dialogRef: MatDialogRef<RefundReceiptDialogComponent>,
    private toast: ToastService,
  ) {}

  getRefundAmount(): number | string | null {
    if (this.data.refund) {
      return this.data.refund.amount ?? null;
    }
    return this.data.payment?.invoice?.refundedAmount ?? this.data.invoice?.refundedAmount ?? null;
  }

  getRefundStatus(): string | null {
    if (this.data.refund) {
      return this.data.refund.status ?? null;
    }
    return this.data.payment?.invoice?.statusDescription ?? null;
  }

  getRefundId(): string | null {
    if (this.data.refund) {
      return this.data.refund.processorRefundId ?? null;
    }
    return this.data.payment?.transactionRefundId ?? null;
  }

  getRefundDate(): string | null {
    if (this.data.refund) {
      return this.data.refund.resolvedAt ?? this.data.refund.createdAt ?? null;
    }
    return this.data.payment?.dateCreated_refund ?? null;
  }

  getRefundTypeLabel(): string | null {
    return this.data.refund ? this.data.refundTypeLabel ?? null : null;
  }

  getRefundStatusLabel(): string | null {
    return this.data.refund ? this.data.refundStatusLabel ?? null : this.getRefundStatus();
  }

  getRefundAuthorizationNumber(): string {
    return this.data.refund?.authorizationNumber
      ?? this.data.refundAuthorizationUnavailableLabel
      ?? RefundReceiptDialogComponent.DEFAULT_UNAVAILABLE_LABEL;
  }

  close(): void {
    this.dialogRef.close();
  }

  async copyRefundReceipt(): Promise<void> {
    const receipt = this.formatReceipt();

    try {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(receipt);
        } catch {
          this.copyWithFallback(receipt);
        }
      } else {
        this.copyWithFallback(receipt);
      }
      this.toast.showSuccess('Recibo copiado al portapapeles.');
    } catch {
      this.toast.showError('No se pudo copiar el recibo.');
    }
  }

  downloadPdf(): void {
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      this.toast.showError('No se pudo abrir la ventana para generar el PDF.');
      return;
    }

    const receipt = this.formatReceipt()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    printWindow.document.write(`<html><head><title>Recibo de Reembolso</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#1f2937}pre{white-space:pre-wrap;font-size:14px;line-height:1.6}</style></head><body><pre>${receipt}</pre></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }

  private formatReceipt(): string {
    const customer = this.data.invoice?.customer ?? {};
    const payment = this.data.payment ?? {};
    const refundAmount = this.getRefundAmount();
    const refundType = this.getRefundTypeLabel();
    const formattedRefundAmount = refundAmount === null ? '-' : `${this.data.currency ?? ''} ${refundAmount}`.trim();

    return [
      'RECIBO DE REEMBOLSO',
      '════════════════════════════════',
      `Nombre: ${[customer.name_customer, customer.lastName_customer].filter(Boolean).join(' ') || '-'}`,
      `Código: ${customer.code_customer ?? '-'}`,
      `Contrato: ${this.data.invoice?.customer_has_plan?.contractNumber_Plan ?? '-'}`,
      `DNI: ${customer.identifier_customer ?? '-'}`,
      '',
      `Monto: ${formattedRefundAmount}`,
      ...(refundType ? [`Tipo: ${refundType}`] : []),
      `ID Reembolso: ${this.getRefundId() ?? '-'}`,
      `Autorización: ${this.getRefundAuthorizationNumber()}`,
      `Fecha: ${this.getRefundDate() ?? '-'}`,
      `Estado: ${this.getRefundStatusLabel() ?? '-'}`,
      '',
      `ID Transacción: ${payment.transactionId ?? '-'}`,
      `Descripción: ${payment.description_payment ?? '-'}`,
      `Monto Original: ${this.data.currency ?? ''} ${payment.amount_payment ?? '-'}`.trim(),
      `Fecha Transacción: ${payment.transactionDate ?? '-'}`,
      '════════════════════════════════',
    ].join('\n');
  }

  private copyWithFallback(receipt: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = receipt;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      if (!document.execCommand('copy')) {
        throw new Error('Clipboard fallback failed');
      }
    } finally {
      document.body.removeChild(textArea);
    }
  }

}
